/**
 * Auth Store — Zustand store for authentication state.
 * Persists token and user data in localStorage.
 *
 * Supports two kinds of session:
 *  - 'user': an individual account, authenticated via the SSO hub (default).
 *  - 'group': a shared seedbed-group login (login_code + password), a
 *    separate mechanism from SSO — see openspec design.md decision #3.
 *    A group session has no `user`, only `group` — progress/quiz fan-out
 *    always applies to every SeedbedPendingMember of the group's roster,
 *    resolved server-side, with no client-tracked "who's active" state —
 *    see openspec/changes/academy-group-progress-fanout/design.md.
 *
 * TODO: this store is coupled to this app's `api` client, `@sentry/react`,
 * and its own `User`/`GroupInfo` types, so it isn't extractable as-is into
 * `@satelab/hooks`. If a second consumer ever needs an equivalent auth
 * store, revisit extracting a `createAuthStore()` factory there instead of
 * duplicating this file (see openspec/changes/ecosystem-cleanup-and-shared-package,
 * decision D6).
 */
import { create } from 'zustand';
import * as Sentry from '@sentry/react';
import { identifyPostHogUser, resetPostHog, identifySentryUser, resetSentryUser } from '@alascorporation/observability';
import { posthog } from '../observability/posthog';
import api from '../lib/api';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'student' | 'instructor' | 'admin' | 'cccd_manager';
  avatar_url?: string;
  bio?: string;
  mustChangePassword?: boolean;
}

// Mirrors platforms/gestion's src/lib/auth.ts mustChangePassword() — see
// openspec/changes/enforce-must-change-password-gate design.md Decision 4.
export function mustChangePassword(user: User | null | undefined): boolean {
  return user?.mustChangePassword === true;
}

// "Staff" = any non-student role, mirroring the API's isStaffRole() — see
// openspec/changes/academy-staff-unrestricted-access/design.md Decision 1.
export function isStaff(user: User | null | undefined): boolean {
  return user?.role !== undefined && user.role !== 'student';
}

interface GroupInfo {
  id: string;
  name: string;
  seedbedId: string;
}

type SessionType = 'user' | 'group';

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  sessionType: SessionType;
  group: GroupInfo | null;

  logout: () => void;
  fetchProfile: () => Promise<void>;
  initialize: () => void;
  loginAsGroup: (token: string, group: GroupInfo) => void;
  setMustChangePassword: (value: boolean) => void;
}

// Read localStorage synchronously at module load time — before any render.
// This eliminates the race where ProtectedRoute renders with isAuthenticated=false
// before the useEffect-based initialize() had a chance to run.
function readStoredAuth(): {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  sessionType: SessionType;
  group: GroupInfo | null;
} {
  try {
    const token = localStorage.getItem('token');
    const sessionType = (localStorage.getItem('sessionType') as SessionType | null) ?? 'user';

    if (sessionType === 'group') {
      const groupStr = localStorage.getItem('group');
      if (token && groupStr) {
        return {
          user: null,
          token,
          isAuthenticated: true,
          sessionType: 'group',
          group: JSON.parse(groupStr) as GroupInfo,
        };
      }
    } else {
      const userStr = localStorage.getItem('user');
      if (token && userStr) {
        const user = JSON.parse(userStr) as User;
        return { user, token, isAuthenticated: true, sessionType: 'user', group: null };
      }
    }
  } catch {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('sessionType');
    localStorage.removeItem('group');
  }
  return { user: null, token: null, isAuthenticated: false, sessionType: 'user', group: null };
}

const storedAuth = readStoredAuth();
if (storedAuth.user) {
  identifySentryUser(Sentry, { id: storedAuth.user.id, email: storedAuth.user.email, username: storedAuth.user.name, role: storedAuth.user.role });
  identifyPostHogUser(posthog, { id: storedAuth.user.id, email: storedAuth.user.email, role: storedAuth.user.role });
} else if (storedAuth.group) {
  // Shared seedbed-group login — the group's own name is the closest thing
  // it has to a username, there's no individual behind the request.
  identifySentryUser(Sentry, { id: storedAuth.group.id, username: storedAuth.group.name, role: 'group' });
}

export const useAuthStore = create<AuthState>((set, get) => ({
  ...storedAuth,
  isLoading: false,

  // initialize() is now a no-op — state is ready before first render.
  // Kept for backward compatibility with App.tsx callers.
  initialize: () => {},

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('sessionType');
    localStorage.removeItem('group');
    set({ user: null, token: null, isAuthenticated: false, sessionType: 'user', group: null });
    resetSentryUser(Sentry);
    resetPostHog(posthog);
    // Best-effort: also revoke the shared '.satelab.org' session cookie, so
    // logging out here doesn't leave a live session id.satelab.org would
    // still silently pick up on the next visit. Group sessions never set it.
    if (get().sessionType === 'user') {
      api.post('/auth/logout').catch(() => {});
    }
  },

  fetchProfile: async () => {
    try {
      const { data } = await api.get('/auth/me');
      const user = data.data;
      localStorage.setItem('user', JSON.stringify(user));
      set({ user });
      identifySentryUser(Sentry, { id: user.id, email: user.email, username: user.name, role: user.role });
      identifyPostHogUser(posthog, { id: user.id, email: user.email, role: user.role });
    } catch {
      // Token may be invalid
      get().logout();
    }
  },

  loginAsGroup: (token, group) => {
    localStorage.setItem('token', token);
    localStorage.setItem('sessionType', 'group');
    localStorage.setItem('group', JSON.stringify(group));
    localStorage.removeItem('user');
    set({ token, sessionType: 'group', group, user: null, isAuthenticated: true });
    identifySentryUser(Sentry, { id: group.id, username: group.name, role: 'group' });
  },

  // Sets the gate flag both in memory and in the persisted `user` object —
  // used at SSO-callback time (flag arrives true) and on successfully
  // completing the change-password form (flag clears to false). A mid-session
  // 403 (packages/api-client's onMustChangePassword) only needs the `true`
  // case since there's no fresh user object to persist alongside it.
  setMustChangePassword: (value) => {
    const current = get().user;
    if (!current) return;
    const user = { ...current, mustChangePassword: value };
    localStorage.setItem('user', JSON.stringify(user));
    set({ user });
  },
}));
